import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'

import Button from '~/components/Buttons/Button'
import InputGroup from '~/components/Inputs/InputGroup'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { keys as groupsKeys, useCreateGroupMutation, useUpdateGroupMutation } from '~/queries/user-groups'

interface FormValues {
  description?: string
  name: string
}

interface FormProps {
  description?: string
  id?: string
  name?: string
  onClose: VoidFunction
  onFailure: (error: unknown) => void
  onSuccess: VoidFunction
}

const validationSchema = Yup.object().shape({
  description: Yup.string(),
  name: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

const ManageGroupForm = ({ description, id, name, onClose, onFailure, onSuccess }: FormProps): ReactElement => {
  /** Local state */

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm({
    defaultValues: { description, name },
    resolver: yupResolver(validationSchema),
  })

  const queryClient = useQueryClient()

  /** Queries & Mutations */

  const { mutateAsync: createGroup } = useCreateGroupMutation()

  const { mutateAsync: updateGroup } = useUpdateGroupMutation()

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    const { description: updatedDescription, name: updatedGroupName } = values
    try {
      if (id) {
        if (updatedDescription !== description || updatedGroupName !== name) {
          await updateGroup({
            description: updatedDescription?.trim() || '',
            groupId: id,
            groupName: updatedGroupName.trim(),
          })
        }
        await queryClient.invalidateQueries({ queryKey: groupsKeys.details(id) })
      } else {
        await createGroup({ description: values.description?.trim() || '', groupName: values.name.trim() })
      }
      await queryClient.invalidateQueries({ queryKey: groupsKeys.list() })
      onSuccess()
    } catch (error) {
      onFailure(error)
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <InputGroup>
          <TextInput
            {...register('name')}
            autoFocus
            error={errors.name?.message}
            label="Name"
            placeholder="Enter name"
            required
          />
          <TextInput
            {...register('description')}
            error={errors.description?.message}
            label="Description"
            placeholder="Enter description"
          />
        </InputGroup>
      </ModalBody>
      <ModalFooter>
        <Button disabled={isSubmitting} ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          {isSubmitting ? 'Loading...' : 'Save'}
        </Button>
      </ModalFooter>
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const ManageGroupModal = ({ id, isOpen, onClose, ...props }: ModalProps): ReactElement => {
  return (
    <Modal className="manage-group-modal" isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>{id ? 'Edit group information' : 'Create a new group'}</ModalHeader>
      <ManageGroupForm {...props} id={id} onClose={onClose} />
    </Modal>
  )
}

export default ManageGroupModal
